import { useEffect, useState } from "react";
import { Sparkles, Loader2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmt } from "@/lib/studioUtils";
import { aiError } from "./aiError";

// Popup opened by "Enter Comment": type a comment for this moment, optionally improve it with AI, then save.
export function CommentDialog({ open, onOpenChange, time, sourceTitle, onSave, initialText = "", editing = false }) {
  const [text, setText] = useState("");
  const [improving, setImproving] = useState(false);
  useEffect(() => { if (open) setText(initialText); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const improve = async () => {
    if (!text.trim()) return;
    setImproving(true);
    try {
      const r = await api.post("/ai/improve", { text: text.trim(), source_title: sourceTitle || "" });
      setText(r.data.text);
    } catch (e) {
      await aiError(e, "Couldn't improve this comment. Your text was kept.");
    }
    setImproving(false);
  };
  const save = () => { if (text.trim() && !improving) { onSave(text.trim()); onOpenChange(false); } };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!improving) onOpenChange(v); }}>
      <DialogContent data-testid="comment-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit comment at" : "Comment at"} <span className="font-mono text-rose-600" data-testid="comment-dialog-time">{fmt(Math.floor(time))}</span></DialogTitle>
        </DialogHeader>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} maxLength={500} autoFocus disabled={improving}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } }}
          data-testid="comment-draft-input" placeholder="Type your reaction to this moment…"
          className="w-full bg-white border border-zinc-200 hover:border-zinc-300 rounded-[4px] px-3 py-2.5 text-sm text-zinc-900 outline-none focus:border-rose-500 transition-colors resize-none disabled:opacity-60" />
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={improve} disabled={improving || !text.trim()} data-testid="comment-improve-btn"
            className="flex items-center gap-1.5 border border-zinc-200 hover:border-rose-400 hover:text-rose-600 disabled:opacity-40 disabled:hover:border-zinc-200 disabled:hover:text-zinc-700 text-zinc-700 text-sm font-medium rounded-[4px] px-4 py-2 transition-colors">
            {improving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} className="text-rose-500" />}
            {improving ? "Improving…" : "Improve with AI"}
          </button>
          <button type="button" onClick={save} disabled={improving || !text.trim()} data-testid="comment-save-btn"
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold rounded-[4px] px-6 py-2 transition-colors">
            <Save size={14} /> Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
